import { useEffect, useRef, useState } from 'react';

import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { OrganizationCodes, CARD_CODES, CardCodes } from '@revenuemarket/bank';
import {
  Dropdown,
  Flex,
  Input as CDSInput,
  Text,
  Colors,
  typographyStyle,
} from '@revenuemarket/cds';
import { thematicColors } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import { formatCompanyTaxNumber } from '@revenuemarket/utils';
import { deformatCompanyTaxNumber } from '@revenuemarket/utils/format';
import { Button, Divider, Form, Input } from 'antd';

import { BusinessRegistrationNumberInfo } from '~apis/bank/getBusinessRegistrationNumbers';
import { loginCardByID } from '~hooks/queries/card/query';
import { useBusinessRegistrationNumbers } from '~hooks/queries/useBusinessRegistrationNumbers';
import useGetCorporateCardConnections from '~hooks/queries/useCorporateCardConnections';
import useGetCorporateCardList from '~hooks/queries/useGetCorporateCardList';
import { useCompanyId } from '~hooks/useCompanyId';
import { validateBusinessRegNumber } from '~utils/businessRegNumber/validator';

import AlertConnectInfo from './AlertConnectInfo';
import { getCardLoginErrorMessage } from './cardLoginErrorMessages';

interface Props {
  onChangeOrganizationCode: (organizationCode: CardCodes) => void;
  onClose: () => void;
  onSuccess: () => void;
  organizationCode: OrganizationCodes;
}

export type CardLoginValues = {
  id: string;
  pw: string;
  organizationCode: OrganizationCodes;
};

export const CardLoginByID: React.FC<Props> = ({
  organizationCode,
  onSuccess,
  onClose,
  onChangeOrganizationCode,
}) => {
  const { companyId } = useCompanyId();
  const { refetch: refetchCardList } = useGetCorporateCardList(companyId);
  const { refetch: refetchCardConnections } = useGetCorporateCardConnections(companyId as string);
  const { mutateAsync: loginByID, isLoading } = loginCardByID();
  const { data: businessNumbersData } = useBusinessRegistrationNumbers();
  const [submittable, setSubmittable] = useState(false);
  const [form] = Form.useForm();

  const [businessNumber, setBusinessNumber] = useState(''); // 포맷되지 않은 초기값
  const [businessNumberError, setBusinessNumberError] = useState<string | undefined>();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputWrapperRef = useRef<HTMLDivElement>(null);

  const values = Form.useWatch([], form);

  // API 응답을 받으면 첫 번째 사업자번호를 기본값으로 설정
  useEffect(() => {
    const businessNumbers = businessNumbersData?.Data?.data?.businessRegNumbers;
    if (businessNumbers && businessNumbers.length > 0 && !businessNumber) {
      setBusinessNumber(businessNumbers[0].businessRegNumber);
    }
  }, [businessNumbersData]);

  useEffect(() => {
    const validation = validateBusinessRegNumber(businessNumber);
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmittable(validation.isValid);
      },
      () => {
        setSubmittable(false);
      },
    );
  }, [values, businessNumber]);

  const handleBusinessNumberChange = (value: string) => {
    setBusinessNumber(value);

    if (value.length > 0) {
      const validation = validateBusinessRegNumber(value);
      setBusinessNumberError(validation.error);
    } else {
      setBusinessNumberError(undefined);
    }
  };

  const handleBusinessNumberSelect = (value: string) => {
    setBusinessNumber(value);
    setBusinessNumberError(undefined);
    setDropdownOpen(false);
  };

  const cardName = CARD_CODES[organizationCode as keyof typeof CARD_CODES];

  const handleIDLogin = async (values: CardLoginValues, organizationCode: OrganizationCodes) => {
    const response = await loginByID({
      companyId,
      password: values.pw,
      loginId: values.id,
      organizationCode,
      businessRegNo: businessNumber,
    });
    if (response.Status === ResponseStatus.SUCCESS) {
      Toast.success('카드 추가가 완료되었습니다.');
      onSuccess();
      refetchCardList();
      refetchCardConnections();
    } else {
      Toast.error(getCardLoginErrorMessage(response.ErrorCode));
    }
  };

  return (
    <Flex direction="column" gap={24} justify="flex-start">
      <Flex direction="column" gap={16}>
        <Text style={{ ...typographyStyle['14_400'], color: Colors.gray800 }}>
          여러 사업자를 운영 중이라면,
          <br />
          등록하는 카드사의 사업자등록번호로 변경해주세요
        </Text>
        <Flex direction="column" gap={4}>
          <Text style={{ ...typographyStyle['14_400'], color: Colors.gray800 }}>
            사업자등록번호
          </Text>
          <div ref={inputWrapperRef}>
            <Dropdown
              isOpen={dropdownOpen}
              setIsOpen={setDropdownOpen}
              menu={{
                items:
                  businessNumbersData?.Data?.data?.businessRegNumbers?.map(
                    (item: BusinessRegistrationNumberInfo) => ({
                      key: item.businessRegNumber,
                      label: item.companyName
                        ? `${formatCompanyTaxNumber(item.businessRegNumber)} (${item.companyName})`
                        : formatCompanyTaxNumber(item.businessRegNumber),
                      onClick: () => handleBusinessNumberSelect(item.businessRegNumber),
                    }),
                  ) || [],
              }}
            >
              <div onClick={() => setDropdownOpen(true)}>
                <CDSInput
                  placeholder="사업자등록번호 10자리"
                  value={businessNumber}
                  formatter={formatCompanyTaxNumber}
                  deformatter={deformatCompanyTaxNumber}
                  inputSize="default"
                  inputStatus={businessNumberError ? 'error' : undefined}
                  additionalText={businessNumberError}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length > 10) {
                      return;
                    }
                    handleBusinessNumberChange(value);
                  }}
                />
              </div>
            </Dropdown>
          </div>
        </Flex>
      </Flex>
      <Divider style={{ margin: 0 }} />
      <Text>{`${cardName} 홈페이지의 로그인 정보를 입력해주세요.`}</Text>
      <Flex gap={8} direction="column">
        <AlertConnectInfo
          organizationCode={organizationCode as CardCodes}
          onChangeOrganizationCode={onChangeOrganizationCode}
        />
      </Flex>

      <Form onFinish={(values) => handleIDLogin(values, organizationCode)} form={form}>
        <Form.Item name="id" rules={[{ required: true, message: '아이디를 입력해주세요.' }]}>
          <Input placeholder="카드사 홈페이지 아이디" prefix={<UserOutlined />} />
        </Form.Item>
        <Form.Item name="pw" rules={[{ required: true, message: '비밀번호를 입력해주세요.' }]}>
          <Input placeholder="카드사 홈페이지 비밀번호" prefix={<LockOutlined />} type="password" />
        </Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          style={{
            width: '100%',
          }}
          disabled={!submittable}
          loading={isLoading}
        >
          로그인
        </Button>
      </Form>
      <Flex>
        <Button
          size="small"
          type="link"
          style={{
            width: 'fit-content',
            color: thematicColors.primary.medium,
          }}
          onClick={() => {
            onClose();
          }}
        >
          {`${cardName} 연결 돌아가기`}
        </Button>
      </Flex>
    </Flex>
  );
};
